import * as d3 from 'd3';
import React from 'react';

import {EvalForecastSparkline} from 'src/lib/api/eval';

type XY = {x: number; y: number};

function toPoints(
  times: string[] | undefined,
  values: Array<number | null> | undefined,
  x: d3.ScaleTime<number, number>,
  dateByT: Map<string, Date>
) {
  if (!times || !values) return [];
  const pts: XY[] = [];
  for (let i = 0; i < Math.min(times.length, values.length); i++) {
    const t = times[i];
    const v = values[i];
    if (v === null || v === undefined) continue;
    const dt = dateByT.get(t);
    if (!dt) continue;
    pts.push({x: x(dt), y: v});
  }
  return pts;
}

export default function ForecastSparkline({
  data,
  width = 210,
  height = 30,
  emptyText = 'No recent forecast',
}: {
  data: EvalForecastSparkline | null | undefined;
  width?: number;
  height?: number;
  emptyText?: string;
}) {
  const forecasts = data?.forecasts ?? [];
  const obs = data?.obs;

  const allTimes = new Set<string>();
  const allValues: number[] = [];
  forecasts.forEach((fc) => {
    fc.times?.forEach((t) => allTimes.add(t));
    fc.values?.forEach((v) => {
      if (v !== null && v !== undefined) allValues.push(v);
    });
  });
  obs?.times?.forEach((t) => allTimes.add(t));
  obs?.values?.forEach((v) => {
    if (v !== null && v !== undefined) allValues.push(v);
  });

  const sortedTimes = Array.from(allTimes).sort();
  if (sortedTimes.length < 2 || allValues.length < 2) {
    return (
      <div
        style={{width, height}}
        className="text-[10px] text-gray-400 flex items-center justify-end pr-1"
      >
        {emptyText}
      </div>
    );
  }

  const margin = {top: 2, right: 2, bottom: 2, left: 28};
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const dateByT = new Map<string, Date>();
  sortedTimes.forEach((t) => {
    const d = new Date(t);
    if (!Number.isNaN(d.getTime())) dateByT.set(t, d);
  });
  const dates = Array.from(dateByT.values()).sort((a, b) => a.getTime() - b.getTime());
  if (dates.length < 2) {
    return (
      <div
        style={{width, height}}
        className="text-[10px] text-gray-400 flex items-center justify-end pr-1"
      >
        {emptyText}
      </div>
    );
  }

  const x = d3
    .scaleTime()
    .domain([dates[0], dates[dates.length - 1]])
    .range([0, innerWidth]);
  const [yMin, yMax] = d3.extent(allValues) as [number, number];
  const y = d3
    .scaleLinear()
    .domain(yMin === yMax ? [yMin - 1, yMax + 1] : [yMin, yMax])
    .range([innerHeight, 0]);

  const fmt = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0));

  const obsPts = toPoints(obs?.times, obs?.values, x, dateByT);
  const forecastPts = forecasts.map((fc) => toPoints(fc.times, fc.values, x, dateByT));
  const hasAnyForecast = forecastPts.some((pts) => pts.length >= 2);

  if (!hasAnyForecast) {
    return (
      <div
        style={{width, height}}
        className="text-[10px] text-gray-400 flex items-center justify-end pr-1"
      >
        {emptyText}
      </div>
    );
  }

  const line = d3
    .line<XY>()
    .x((d) => d.x)
    .y((d) => y(d.y));

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <g transform={`translate(${margin.left},${margin.top})`}>
        <text x={-3} y={0} dy="0.7em" textAnchor="end" fill="#999" fontSize="8px">
          {fmt(yMax)}
        </text>
        <text x={-3} y={innerHeight} dy="0.1em" textAnchor="end" fill="#999" fontSize="8px">
          {fmt(yMin)}
        </text>

        {obsPts.length >= 2 && (
          <path
            d={line(obsPts) ?? undefined}
            fill="none"
            stroke="#333"
            strokeWidth="1.5"
            opacity="0.8"
          />
        )}

        {forecastPts
          .slice()
          .reverse()
          .map((pts, idxFromOldest) => {
            if (pts.length < 2) return null;
            const total = forecastPts.length;
            const i = total - 1 - idxFromOldest;
            const opacity = i === 0 ? 1.0 : 0.3 + (0.4 * (total - 1 - i)) / Math.max(1, total - 1);
            return (
              <path
                key={idxFromOldest}
                d={line(pts) ?? undefined}
                fill="none"
                stroke="rgb(37, 99, 235)"
                strokeWidth={i === 0 ? 1.5 : 1}
                opacity={opacity}
              />
            );
          })}
      </g>
    </svg>
  );
}
